import { useEffect, useRef } from "react";
import type { EnergyType } from "../../shared/energy";
import { createFlameRenderer, FLAME_SECONDS } from "../lib/flameShader";

/** Effects play only where there is hover and motion is welcome (matches the CSS media query). */
const canAnimate = () =>
  typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (prefers-reduced-motion: no-preference)").matches === true;

/** Canvas resolution cap: the effect is small and short, no need for full retina. */
const MAX_DPR = 1.5;

/**
 * Fire — Flamethrower, drawn with WebGL (lib/flameShader) on hover of the card: one jet, then the
 * context is freed so a grid full of fire cards never holds more than the one being hovered.
 */
function Flamethrower() {
  const box = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = box.current;
    const host = el?.closest(".fx-host");
    if (!el || !host) return;
    let frame = 0;
    let renderer: ReturnType<typeof createFlameRenderer> = null;
    let canvas: HTMLCanvasElement | null = null;
    // A canvas whose WebGL context was freed can't draw again: each attack gets a fresh one.
    const stop = () => {
      cancelAnimationFrame(frame);
      renderer?.dispose();
      renderer = null;
      canvas?.remove();
      canvas = null;
    };
    const start = () => {
      if (!canAnimate()) return;
      stop();
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      canvas = document.createElement("canvas");
      canvas.className = "fx-canvas";
      el.appendChild(canvas);
      canvas.width = Math.max(1, Math.round(el.clientWidth * dpr));
      canvas.height = Math.max(1, Math.round(el.clientHeight * dpr));
      renderer = createFlameRenderer(canvas);
      if (!renderer) return stop();
      const t0 = performance.now();
      const tick = (now: number) => {
        const t = (now - t0) / 1000;
        if (!renderer) return;
        if (t >= FLAME_SECONDS) return stop();
        renderer.draw(t);
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };
    host.addEventListener("mouseenter", start);
    return () => {
      host.removeEventListener("mouseenter", start);
      stop();
    };
  }, []);

  return <span ref={box} className="fx-canvas-box" />;
}

/**
 * Hover effect on a Pokémon card's picture, by type: one "attack" per hover — Thunder, Surf,
 * Flamethrower… Pure CSS (styles.css "Card type effects") except the fire, drawn with WebGL. The host
 * element needs the `fx-host` class. Only on devices that hover and without reduced motion.
 * Decorative, hidden from screen readers.
 */
export function CardFx({ type }: { type: EnergyType | null | undefined }) {
  if (!type) return null;
  return (
    <span className={`card-fx fx-${type.toLowerCase()}`} aria-hidden="true">
      {type === "Fire" ? (
        <Flamethrower />
      ) : (
        <>
          <i />
          <i />
          <i />
          <i />
          <i />
        </>
      )}
    </span>
  );
}
